import React from 'react';
import * as types from '../../api/types';
import { printableChecklistGroups } from '../jobChecklistUi';

/**
 * A Job's checklist on a service order / work order (tracker item 44): one
 * box per step, empty when open and filled when ticked, with a "Required"
 * marker. Renders nothing when the job has no steps. The fill is a text
 * glyph inside a bordered box, because browsers drop background graphics
 * when printing. Groups may break across pages; a group's heading stays with
 * its first step.
 */
export function ChecklistPrintBlock({ checklist }: { checklist: types.JobChecklist | null | undefined }) {
  const groups = printableChecklistGroups(checklist);
  if (groups.length === 0) return null;
  return (
    <section className="mt-8" aria-label="Checklist" data-checklist-print>
      <h2 className="mb-2 text-base font-semibold">
        Checklist <span className="text-sm font-normal text-gray-700">({checklist!.completed}/{checklist!.total} done)</span>
      </h2>
      <div className="space-y-4">
        {groups.map((group) => (
          <div key={`${group.sourceTemplateId ?? ''}:${group.groupName}`}>
            <h3 className="mb-1 text-sm font-semibold uppercase tracking-wide text-gray-700 [break-after:avoid]">{group.groupName}</h3>
            <ul className="space-y-1.5">
              {group.steps.map((step) => (
                <li key={step.id} className="flex items-start gap-3 [break-inside:avoid]" data-checklist-step={step.completed ? 'done' : 'open'}>
                  <span
                    aria-hidden="true"
                    className="mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center border-2 border-gray-900 text-[0.7rem] leading-none"
                  >
                    {step.completed ? '■' : ''}
                  </span>
                  <span className="sr-only">{step.completed ? 'Done: ' : 'Not done: '}</span>
                  <span className="min-w-0 break-words">
                    {step.text}
                    {step.required && <span className="ml-2 text-xs font-bold uppercase tracking-wide" data-required>Required</span>}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
}
